import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

export type WhatsAppProductDisplayPreviewMode = "LIST" | "CAROUSEL";

const WA = {
  wallpaper: "#f0f2f5",
  wallpaperPattern: [
    `radial-gradient(circle at 12% 18%, rgba(102,119,129,0.07) 0%, transparent 42%)`,
    `radial-gradient(circle at 88% 12%, rgba(102,119,129,0.06) 0%, transparent 38%)`,
    `radial-gradient(circle at 70% 82%, rgba(102,119,129,0.05) 0%, transparent 40%)`,
    `radial-gradient(circle at 25% 75%, rgba(102,119,129,0.06) 0%, transparent 35%)`,
  ].join(", "),
  card: "#ffffff",
  text: "#111b21",
  textSecondary: "#54656f",
  timestamp: "#667781",
  divider: "#eeeeee",
  accentGreen: "#007a5e",
  imageAreaGradient: "linear-gradient(145deg, #f5f6f6 0%, #eceff1 50%, #e8eaed 100%)",
  imageFadeToCard: "linear-gradient(to bottom, rgba(255,255,255,0) 55%, rgba(255,255,255,0.92) 100%)",
  cardShadow: "0 1px 0.5px rgba(11,20,26,0.12), 0 2px 8px rgba(11,20,26,0.08)",
  navBtnBg: "#ffffff",
  navBtnIcon: "#111b21",
  navBtnShadow: "0 2px 8px rgba(11,20,26,0.14), 0 1px 2px rgba(11,20,26,0.08)",
  chevronMuted: "#667781",
  /** Largura próxima à bolha máx. de produto no WhatsApp (telefone) */
  listCardWidth: 262,
  listRadius: "12px",
  listImageRadius: "8px",
};

const LIST_DEMO = {
  headline: "1 - Camiseta básica",
  price: "A partir de: R$ 49,90",
  time: "10:07 AM",
};

const CAROUSEL_DEMOS = [
  { name: "Camiseta básica", detail: "Sem variações • A partir de: R$ 49,90" },
  { name: "Tênis casual", detail: "Sem variações • A partir de: R$ 199,90" },
  { name: "Boné trucker com aba curva", detail: "Sem variações • A partir de: R$ 39,90" },
  { name: "Meia esportiva cano médio", detail: "Sem variações • A partir de: R$ 24,90" },
];

const CAROUSEL_CARD_WIDTH_PX = 158;
const CAROUSEL_GAP_PX = 20;

type Props = {
  mode: WhatsAppProductDisplayPreviewMode;
  title: string;
  disclaimer: string;
  virtualAssistantLabel: string;
  productNamePrefix: string;
  detailsHeader: string;
  imagePlaceholder: string;
  productButtonPrefix: string;
};

/** Lista: imagem ocupa ~65–70% da altura do card; área hero maior. */
function ListHeroImage({ label }: { label: string }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 186,
        aspectRatio: "3 / 3.45",
        maxHeight: 238,
        bgcolor: WA.card,
        background: WA.imageAreaGradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: WA.listImageRadius,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          top: "42%",
          background: WA.imageFadeToCard,
          pointerEvents: "none",
          borderRadius: WA.listImageRadius,
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: WA.textSecondary,
          px: 2,
          textAlign: "center",
          position: "relative",
          zIndex: 1,
          fontSize: "0.8125rem",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CarouselProductImage({ label }: { label: string }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "4 / 3",
        maxHeight: 140,
        bgcolor: WA.card,
        background: WA.imageAreaGradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          top: "45%",
          background: WA.imageFadeToCard,
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: WA.textSecondary, px: 1, textAlign: "center", position: "relative", zIndex: 1 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CarouselRow({
  productNamePrefix,
  detailsHeader,
  imagePlaceholder,
  productButtonPrefix,
}: Pick<Props, "productNamePrefix" | "detailsHeader" | "imagePlaceholder" | "productButtonPrefix">) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const syncArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const max = scrollWidth - clientWidth;
    setCanPrev(scrollLeft > 6);
    setCanNext(scrollLeft < max - 6);
  }, []);

  useEffect(() => {
    syncArrows();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => syncArrows());
    ro.observe(el);
    return () => ro.disconnect();
  }, [syncArrows]);

  const step = CAROUSEL_CARD_WIDTH_PX + CAROUSEL_GAP_PX;

  const scrollByDir = (dir: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const btnSx = {
    flexShrink: 0,
    width: 40,
    height: 40,
    bgcolor: WA.navBtnBg,
    color: WA.navBtnIcon,
    boxShadow: WA.navBtnShadow,
    "&:hover": { bgcolor: "#fafafa", boxShadow: WA.navBtnShadow },
    "&.Mui-disabled": { opacity: 0.4, bgcolor: WA.navBtnBg },
  } as const;

  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%" }}>
      <IconButton
        size="small"
        aria-label="Ver produtos anteriores"
        disabled={!canPrev}
        onClick={() => scrollByDir(-1)}
        sx={btnSx}
      >
        <ChevronLeftIcon sx={{ fontSize: 24 }} />
      </IconButton>

      <Box
        ref={scrollRef}
        onScroll={syncArrows}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          gap: `${CAROUSEL_GAP_PX}px`,
          overflowX: "auto",
          overflowY: "hidden",
          py: 0.25,
          scrollBehavior: "smooth",
          scrollSnapType: "x proximity",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
            width: 0,
            height: 0,
          },
        }}
      >
        {CAROUSEL_DEMOS.map((item, index) => (
          <Box
            key={index}
            sx={{
              flex: "0 0 auto",
              width: CAROUSEL_CARD_WIDTH_PX,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
              scrollSnapAlign: "start",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: WA.cardShadow,
              bgcolor: WA.card,
              border: `1px solid ${WA.divider}`,
            }}
          >
            <Box sx={{ flexShrink: 0, borderRadius: "10px 10px 0 0", overflow: "hidden" }}>
              <CarouselProductImage label={imagePlaceholder} />
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                bgcolor: WA.card,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: WA.text,
                  fontSize: "0.65rem",
                  lineHeight: 1.4,
                  display: "block",
                  fontWeight: 700,
                }}
              >
                {productNamePrefix} {item.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: WA.text,
                  fontSize: "0.6rem",
                  fontWeight: 400,
                  display: "block",
                  mt: 0.5,
                }}
              >
                {detailsHeader}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: WA.text,
                  fontSize: "0.6rem",
                  fontWeight: 400,
                  display: "block",
                  mt: 0.25,
                  lineHeight: 1.35,
                }}
              >
                {item.detail}
              </Typography>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                marginTop: "auto",
                borderTop: `1px solid ${WA.divider}`,
                bgcolor: WA.card,
                px: 1.25,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <ReplyRoundedIcon sx={{ fontSize: 17, color: WA.accentGreen }} />
              <Typography
                variant="caption"
                sx={{ color: WA.accentGreen, fontSize: "0.74rem", fontWeight: 600 }}
              >
                {productButtonPrefix}
                {index + 1}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <IconButton
        size="small"
        aria-label="Ver próximos produtos"
        disabled={!canNext}
        onClick={() => scrollByDir(1)}
        sx={btnSx}
      >
        <ChevronRightIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </Stack>
  );
}

export default function WhatsAppProductDisplayPreview({
  mode,
  title,
  disclaimer,
  virtualAssistantLabel,
  productNamePrefix,
  detailsHeader,
  imagePlaceholder,
  productButtonPrefix,
}: Props) {
  return (
    <Stack spacing={1.25} sx={{ height: "100%" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {title}
      </Typography>

      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: WA.wallpaper,
          backgroundImage: WA.wallpaperPattern,
          backgroundSize: "100% 100%",
          p: 1.5,
          minHeight: mode === "LIST" ? 342 : 200,
        }}
      >
        {mode === "LIST" ? (
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box
              sx={{
                width: WA.listCardWidth,
                maxWidth: "100%",
                borderRadius: WA.listRadius,
                overflow: "hidden",
                boxShadow: WA.cardShadow,
                bgcolor: WA.card,
                border: `1px solid ${WA.divider}`,
              }}
            >
              <Box sx={{ px: 1, pt: 1, pb: 0 }}>
                <ListHeroImage label={imagePlaceholder} />
              </Box>
              <Box
                sx={{
                  px: 2,
                  pt: 2,
                  pb: 1.25,
                  bgcolor: WA.card,
                }}
              >
                <Typography
                  variant="body2"
                  component="p"
                  sx={{
                    color: WA.text,
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    lineHeight: 1.35,
                    m: 0,
                  }}
                >
                  {virtualAssistantLabel}
                </Typography>
                <Typography
                  variant="body2"
                  component="p"
                  sx={{
                    color: WA.text,
                    fontWeight: 400,
                    fontSize: "0.8125rem",
                    lineHeight: 1.38,
                    mt: 0.75,
                    m: 0,
                  }}
                >
                  {LIST_DEMO.headline}
                </Typography>
                <Typography
                  variant="body2"
                  component="p"
                  sx={{
                    color: WA.text,
                    fontWeight: 400,
                    fontSize: "0.8125rem",
                    lineHeight: 1.38,
                    mt: 0.85,
                    m: 0,
                  }}
                >
                  {LIST_DEMO.price}
                </Typography>
                <Stack direction="row" justifyContent="flex-end" alignItems="baseline" sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: WA.timestamp, fontSize: "0.6875rem", letterSpacing: "0.01em" }}
                  >
                    {LIST_DEMO.time}
                  </Typography>
                </Stack>
              </Box>
            </Box>
            <Box
              aria-hidden
              sx={{
                mt: 1,
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: WA.navBtnBg,
                color: WA.chevronMuted,
                boxShadow: WA.navBtnShadow,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RedoRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
          </Stack>
        ) : (
          <CarouselRow
            productNamePrefix={productNamePrefix}
            detailsHeader={detailsHeader}
            imagePlaceholder={imagePlaceholder}
            productButtonPrefix={productButtonPrefix}
          />
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
        {disclaimer}
      </Typography>
    </Stack>
  );
}
